import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ShoppingCart, 
  Filter, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  AlertCircle,
  Plus,
  Search
} from 'lucide-react';
import { usePurchaseOrders } from '../hooks/usePurchaseOrders';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { useAccessibleLocations } from '@/hooks/useAccessibleLocations';
import { PurchaseOrderModal } from './PurchaseOrderModal';
import type { PurchaseOrder, PurchaseOrderStatus, PurchaseOrderFormData } from '@/types/purchase-order.types';

interface PurchaseOrdersPageProps {
  locationId?: string;
}

export const PurchaseOrdersPage: React.FC<PurchaseOrdersPageProps> = ({ locationId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedLocation, user } = useAuth();
  const { locationsWithAll: locations, loading: locationsLoading } = useAccessibleLocations();
  const currentLocationId = locationId || selectedLocation?.id || 'loc-1';

  // Check if user is CAJERO (positionId: 3)
  const isCajero = user?.positionId === 3;

  const [newOrderModalOpen, setNewOrderModalOpen] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    orders,
    stats,
    suppliers,
    filters,
    loading,
    error,
    updateFilters,
    fetchOrders,
    createOrder,
    approveOrder,
    receiveOrder,
    cancelOrder
  } = usePurchaseOrders(currentLocationId, isCajero);

  // Estado local para el filtro de status que ahora será con botones
  // Para cajeros (positionId: 3), cargar por defecto las órdenes aprobadas
  const defaultStatus = isCajero ? 'approved' : 'all';
  const [selectedStatus, setSelectedStatus] = useState<string>(filters.status || defaultStatus);

  // Auto-open modal when in /new route
  useEffect(() => {
    if (location.pathname === '/purchase-orders/new') {
      setNewOrderModalOpen(true);
    }
  }, [location.pathname]);

  // Set default filter for cajeros to 'approved' when component mounts
  useEffect(() => {
    if (isCajero && selectedStatus === 'all') {
      setSelectedStatus('approved');
      updateFilters({ status: 'approved' });
    }
  }, [isCajero, updateFilters]);

  const getStatusBadge = (status: PurchaseOrderStatus) => {
    const variants = {
      pending: 'secondary',
      approved: 'default', 
      received: 'success',
      cancelled: 'destructive'
    } as const;
    
    const labels = {
      pending: 'Pendiente',
      approved: 'Aprobada',
      received: 'Recibida',
      cancelled: 'Cancelada'
    };
    
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      received: Package,
      cancelled: XCircle
    };
    
    const Icon = icons[status];
    
    return (
      <Badge variant={variants[status]} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {labels[status]}
      </Badge>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status filter options
  const statusFilters: Array<{ key: string, label: string, activeBg: string, borderColor: string, countColor: string }> = [
    { key: 'all', label: 'Todas', activeBg: 'bg-gray-700 text-white', borderColor: 'border-l-gray-400', countColor: 'text-gray-500' },
    { key: 'pending', label: 'Pendientes', activeBg: 'bg-amber-500 text-white', borderColor: 'border-l-amber-500', countColor: 'text-amber-600' },
    { key: 'approved', label: 'Aprobadas', activeBg: 'bg-sky-500 text-white', borderColor: 'border-l-sky-500', countColor: 'text-sky-600' },
    { key: 'received', label: 'Recibidas', activeBg: 'bg-emerald-500 text-white', borderColor: 'border-l-emerald-500', countColor: 'text-emerald-600' },
    { key: 'cancelled', label: 'Canceladas', activeBg: 'bg-rose-500 text-white', borderColor: 'border-l-rose-500', countColor: 'text-rose-600' }
  ];

  // Get count for each status
  const getStatusCount = (status: string) => {
    if (status === 'all') return orders.length;
    return orders.filter(order => order.status === status).length;
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string) => {
    setSelectedStatus(status);
    updateFilters({ status: status === 'all' ? '' : status });
  };

  // Filtrar y ordenar órdenes:
  // - Para NO cajeros: mostrar TODAS las órdenes
  // - Para cajeros: mostrar aprobadas (sin límite) y recibidas (últimas 24 horas)
  const getFilteredAndSortedOrders = () => {
    // Para usuarios que NO son cajeros, mostrar todas las órdenes sin filtrar
    if (!isCajero) {
      return [...orders].sort((a, b) => {
        const statusPriority: Record<string, number> = {
          'approved': 1,
          'pending': 2,
          'received': 3,
          'cancelled': 4
        };
        const statusDiff = (statusPriority[a.status] || 5) - (statusPriority[b.status] || 5);
        if (statusDiff !== 0) return statusDiff;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    }

    // Para cajeros: filtrar por estado
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const filteredOrders = orders.filter(order => {
      // Siempre mostrar pendientes y aprobadas
      if (order.status === 'pending' || order.status === 'approved') return true;

      // Mostrar recibidas solo si fueron recibidas desde ayer
      if (order.status === 'received') {
        const receivedDate = order.receivedAt ? new Date(order.receivedAt) : new Date(order.updatedAt);
        return receivedDate >= yesterday;
      }

      return false;
    });

    // Ordenar: aprobadas primero, luego por fecha
    return filteredOrders.sort((a, b) => {
      const statusPriority: Record<string, number> = {
        'approved': 1,
        'pending': 2,
        'received': 3,
        'cancelled': 4
      };
      const statusDiff = (statusPriority[a.status] || 5) - (statusPriority[b.status] || 5);
      if (statusDiff !== 0) return statusDiff;

      // Luego por fecha (más recientes primero)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  };

  const sortedOrders = getFilteredAndSortedOrders();

  const handleViewDetails = (order: PurchaseOrder) => {
    navigate(`/purchase-orders/${order.id}`);
  };

  const handleApproveClick = (order: PurchaseOrder) => {
    setSelectedOrder(order);
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async () => {
    if (selectedOrder) {
      setIsProcessing(true);
      try {
        await approveOrder(selectedOrder.id, 'Aprobada desde interfaz de gestión');
        setShowApproveModal(false);
        setSelectedOrder(null);
      } catch (error) {
        console.error('Error approving order:', error);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleReceive = (order: PurchaseOrder) => {
    navigate(`/purchase-orders/${order.id}/receive`);
  };


  const handleCancelClick = (order: PurchaseOrder) => {
    setSelectedOrder(order);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (selectedOrder) {
      setIsProcessing(true);
      try {
        await cancelOrder(selectedOrder.id, 'Cancelada desde interfaz de gestión');
        setShowCancelModal(false);
        setSelectedOrder(null);
      } catch (error) {
        console.error('Error cancelling order:', error);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleCreateOrder = async (orderData: PurchaseOrderFormData): Promise<boolean> => {
    try {
      const newOrder = await createOrder(orderData);
      if (newOrder !== null) {
        setNewOrderModalOpen(false);
        if (location.pathname === '/purchase-orders/new') {
          navigate('/purchase-orders');
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error creating purchase order:', error);
      return false;
    }
  };


  if (loading && orders.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-700">Cargando órdenes de compra...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error al cargar órdenes</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Órdenes de Compra</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Gestión de órdenes de compra
          </p>
        </div>
        {!isCajero && (
          <Button
            onClick={() => navigate('/purchase-orders/new')}
            className="bg-macartur-primary hover:bg-macartur-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Orden
          </Button>
        )}
      </div>


      {/* Filters - Hidden for CAJERO */}
      {!isCajero && (
        <Card>
          <CardContent className="pt-4 pb-3 space-y-2">
            {/* Fila 1: Filtros de busqueda */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Desde:</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilters({ dateFrom: e.target.value })}
                  className="text-sm h-9 w-40"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Hasta:</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilters({ dateTo: e.target.value })}
                  className="text-sm h-9 w-40"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Proveedor:</label>
                <Select
                  value={filters.supplier || "all"}
                  onValueChange={(value) => updateFilters({ supplier: value === "all" ? "" : value })}
                >
                  <SelectTrigger className="h-9 w-48 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los Proveedores</SelectItem>
                    {suppliers.map(supplier => (
                      <SelectItem key={supplier.id} value={supplier.businessName}>
                        {supplier.businessName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Local:</label>
                <Select
                  value={filters.location || "all"}
                  onValueChange={(value) => updateFilters({ location: value === "all" ? "" : value })}
                >
                  <SelectTrigger className="h-9 w-48 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(location => (
                      <SelectItem key={location.id} value={location.id.toString()}>
                        {location.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                className="h-9 bg-macartur-primary hover:bg-macartur-primary/90"
                onClick={() => fetchOrders()}
              >
                <Search className="mr-1 h-4 w-4" />
                Buscar
              </Button>
            </div>
            {/* Fila 2: Chips de estado */}
            <div className="flex flex-wrap items-center gap-1.5">
              {statusFilters.map((sf) => {
                const count = getStatusCount(sf.key);
                const isActive = selectedStatus === sf.key;
                return (
                  <button
                    key={sf.key}
                    onClick={() => handleStatusFilterChange(sf.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? sf.activeBg
                        : `bg-white border border-gray-200 border-l-[3px] ${sf.borderColor} text-gray-700 hover:bg-gray-50`
                    }`}
                  >
                    {sf.label}
                    <span className={`text-xs font-bold ${isActive ? 'text-white' : sf.countColor}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Órdenes de Compra ({orders.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No hay órdenes de compra</h3>
              <p className="text-gray-500">Las órdenes aparecerán aquí cuando se generen</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedOrders.map((order) => (
                <div
                  key={order.id}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {/* Fila 1: PO + Proveedor badge + Estado + Fecha a la derecha */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-lg">{order.orderNumber}</span>
                      <Badge className="text-xs bg-slate-600 hover:bg-slate-600 text-white">
                        {order.supplierName}
                      </Badge>
                      {getStatusBadge(order.status)}
                    </div>
                    <span className="text-xs text-gray-500">{formatDate(order.createdAt)}</span>
                  </div>

                  {/* Fila 2: Info a la izquierda + Botones a la derecha */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                      <span><span className="font-bold">Sucursal:</span> {order.location?.nombre || 'Sin asignar'}</span>
                      <span><span className="font-bold">Items:</span> {order.items.length}</span>
                      {!isCajero && (
                        <span><span className="font-bold">Total:</span> {formatCurrency(order.total)}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                        {!isCajero && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(order)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                        )}

                        {order.status === 'pending' && (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleApproveClick(order)}
                              disabled={isProcessing}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aprobar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelClick(order)}
                              disabled={isProcessing}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Cancelar
                            </Button>
                          </>
                        )}

                        {order.status === 'approved' && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleReceive(order)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Package className="w-4 h-4 mr-1" />
                            Recibir
                          </Button>
                        )}
                      </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>


      {/* New Order Modal */}
      <PurchaseOrderModal
        isOpen={newOrderModalOpen}
        onClose={() => {
          setNewOrderModalOpen(false);
          if (location.pathname === '/purchase-orders/new') {
            navigate('/purchase-orders');
          }
        }}
        onSubmit={handleCreateOrder}
        suppliers={suppliers}
        loading={loading}
      />

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmar Aprobación
            </h3>
            <p className="text-gray-600 mb-6">
              ¿Está seguro que desea aprobar la orden de compra <span className="font-semibold">{selectedOrder?.orderNumber}</span>?
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowApproveModal(false);
                  setSelectedOrder(null);
                }}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleApproveConfirm}
                disabled={isProcessing}
                className="bg-green-600 hover:bg-green-700"
              >
                {isProcessing ? 'Procesando...' : 'Aprobar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmar Cancelación
            </h3>
            <p className="text-gray-600 mb-6">
              ¿Está seguro que desea cancelar la orden de compra <span className="font-semibold">{selectedOrder?.orderNumber}</span>?
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedOrder(null);
                }}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCancelConfirm}
                disabled={isProcessing}
                className="bg-red-600 hover:bg-red-700"
              >
                {isProcessing ? 'Procesando...' : 'Confirmar Cancelación'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PurchaseOrdersPage;